import React, { useContext, useState } from 'react'
import { observer } from 'mobx-react'

// Components
import { Button } from '../../components/Button'
import { FileUploader } from '../../components/FileUploader'
import { PageContainer } from '../../components/PageContainer'
import { StateContainer } from '../../components/StateContainer'

// Service & Stores
import { resetClientData, updateClient } from '../../services/clients.service'
import { ClientDashboardStoreContext } from '../../stores/ClientDashboardStore'
import { UserStoreContext } from '../../stores/UserStore'

// Utils
import { toast } from '../../utils/helpers'

const Misc = observer(() => {
  // Context
  const { client, setClient } = useContext(ClientDashboardStoreContext)
  const { user } = useContext(UserStoreContext)

  // State
  const [upload, setUpload] = useState(false)

  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')

  const renderLogo = () => {
    if (client.signedLogoUrl && !upload) {
      return (
        <>
          <img
            src={client.signedLogoUrl}
            alt="Client Logo"
            className="rounded-md border border-gray-300 object-cover"
          />

          <Button
            className="my-4"
            background="bg-blue"
            label="Replace"
            onClick={() => setUpload(true)}
          />
        </>
      )
    }

    return (
      <div className="flex flex-col gap-4">
        <div className="w-80">
          <FileUploader
            acceptedFileTypes={['image/jpeg', 'image/png', 'image/svg+xml']}
            filePrefix="client-logos/"
            handleUploadToServer={async (file) => {
              const response = await updateClient(
                {
                  id: client.id,
                  logoUrl: file.fileUploadName,
                },
                handleError,
                () => {},
                () => {},
              )

              if (response) {
                handleSuccess('Client logo updated.')
                setClient(response)
                setUpload(false)
              }
            }}
            id="file"
            maxFiles={1}
            type="aws"
          />
        </div>
      </div>
    )
  }

  return (
    <PageContainer>
      <StateContainer>
        <div className="flex size-full flex-col overflow-y-auto bg-background">
          <div className="space-y-6">
            <h2 className="pt-1 text-xl font-semibold leading-6 text-gray-900">Misc. Settings</h2>
            <div className="mt-2 border-t border-gray-100">
              <dl className="divide-y divide-gray-100">
                <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                  <dt className="text-sm font-medium leading-6 text-gray-900">Client Logo</dt>
                  <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                    <div className="pb-4">
                      <p className="pb-2 text-sm leading-6 text-gray-500">
                        If set, this logo will be used in reports.
                      </p>
                      {renderLogo()}

                      <p className="pb-2 text-sm leading-6 text-gray-500"></p>
                    </div>
                  </dd>
                </div>

                {user?.isStaff && (
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                    <dt className="text-sm font-medium leading-6 text-gray-900">
                      Reset Client Data
                    </dt>
                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                      <div className="pb-4">
                        <p className="pb-2 text-sm leading-6 text-gray-500">
                          Reset all data for this client.
                        </p>

                        <Button
                          background="bg-red"
                          label="Reset Data"
                          onClick={() =>
                            resetClientData(client.id, handleError, () => {}, handleSuccess)
                          }
                          className="self-center"
                          confirm={{
                            title: `Clear all data for Client ${client.name}`,
                            type: 'warning',
                            message: `Are you sure you want to delete all data for ${client.name}? This action cannot be undone.`,
                          }}
                          showConfirmDialog
                        />
                      </div>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        </div>
      </StateContainer>
    </PageContainer>
  )
})

export default Misc
